import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FlowLayout, Input } from '@/ds'
import { useAppNavigate } from '@/lib/query-params'
import { checkinService, stepErrors } from '@/services/checkin'
import type { GuestField } from '@/services/checkin'
import { holderTraveler, useCheckinFlow } from '../CheckinFlow'
import { backLabelKey, neighbours, progressFor, stepPath } from '../flow'
import { errorsAfterGuestChange, GuestFields } from '../fields'
import type { FieldErrors } from '../fields'
import { phoneError } from '../phone'
import { useSave } from '../useSave'

/** El país va primero: determina desde el inicio si el segundo apellido y el documento son obligatorios. */
const FIELDS: GuestField[] = ['countryId', 'name', 'surname1', 'surname2', 'email']

/** Paso 2 · Tus datos (CheckIn.dc.html step=2). Precarga D23; apellidos en dos campos (D31); móvil sin endpoint (D29). */
export function Datos() {
  const { t } = useTranslation()
  const { go } = useAppNavigate()
  const { stay, draft, updateGuest, update } = useCheckinFlow()
  const [errors, setErrors] = useState<FieldErrors>({})
  const [phoneTouched, setPhoneTouched] = useState(false)
  const { saving, error, run } = useSave()
  const { prev, next } = neighbours('2', stay, draft.persona, draft.method, draft.entry)
  const isHolder = draft.persona === 'holder'
  const prefilled = isHolder && holderTraveler(stay) !== null

  const phoneMsg = (() => {
    if (!isHolder || !phoneTouched) return undefined
    const e = phoneError(draft.phone)
    return e === 'prefix' ? t('v2.checkin.datos.prefijo') : e === 'digits' ? t('v2.checkin.invitar.faltanCifras') : undefined
  })()

  const onBlurField = (f: GuestField) => {
    const e = stepErrors([f], stay, draft.guest)
    setErrors((prevErrors) => ({ ...prevErrors, [f]: e[f] }))
  }
  const onGuestChange = (patch: Partial<typeof draft.guest>) => {
    updateGuest(patch)
    setErrors((current) => errorsAfterGuestChange(current, patch, stay, draft.guest))
  }

  const submit = async () => {
    const e = stepErrors(FIELDS, stay, draft.guest)
    setErrors(e)
    setPhoneTouched(true)
    if (Object.keys(e).length > 0 || (isHolder && phoneError(draft.phone) !== null)) return
    const ok = isHolder ? await run(() => checkinService.setPhone(stay, draft.phone.trim()).then(() => true)) : true
    if (ok) go(stepPath(next))
  }

  return (
    <FlowLayout
      progress={{ ...progressFor('2', stay, draft.persona, draft.method, draft.entry), backLabel: t(backLabelKey(prev)), onBack: () => go(stepPath(prev)) }}
      title={t('v2.checkin.datos.titulo')}
      intro={prefilled ? t('v2.checkin.datos.intro') : undefined}
      primary={{ label: t('v2.checkin.continuar'), onClick: () => void submit(), loading: saving }}
      footerMessage={error}
    >
      <GuestFields stay={stay} guest={draft.guest} fields={FIELDS} errors={errors} onChange={onGuestChange} onBlurField={onBlurField} focusRequest={draft.entry === 'manual' ? `manual-data-${draft.persona}` : undefined} />
      {isHolder && (
        <>
          <Input label={t('v2.checkin.datos.movil')} type="tel" inputMode="tel" autoComplete="tel" value={draft.phone} onChange={(e) => update({ phone: e.target.value })} onBlur={() => setPhoneTouched(true)} {...(phoneMsg ? { error: phoneMsg } : {})} />
          <p className="checkin__note">{t(checkinService.notices.phoneReachesProperty ? 'v2.checkin.datos.movilNota' : 'v2.checkin.datos.movilNotaDispositivo')}</p>
        </>
      )}
    </FlowLayout>
  )
}
